import { useMemo } from "react";
import { useRoute, useSearch, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, RefreshCw, ArrowLeft, Copy } from "lucide-react";
import { fetchSqlSyncPendingData, SqlSyncPendingDataResponse } from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";

const formatDate = (value: any) => {
  const date = value ? new Date(value) : null;
  return date && !isNaN(date.getTime()) ? date.toISOString() : String(value ?? "");
};

const normalizeRow = (row: Record<string, any>) => {
  // Convert Date objects to ISO strings for consistency
  const normalized: Record<string, any> = {};
  Object.entries(row || {}).forEach(([key, val]) => {
    if (val instanceof Date) {
      normalized[key] = val.toISOString();
    } else {
      normalized[key] = val;
    }
  });
  return normalized;
};

// Single-flag architecture: fixed environment
const DEFAULT_ENVIRONMENT: "Staging" | "Production" = "Production";

export default function SqlSyncPendingDataPage() {
  const [, params] = useRoute("/sql-sync/pending/:entityType");
  const search = useSearch();
  const [, setLocation] = useLocation();
  const entityType = params?.entityType;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const environment = DEFAULT_ENVIRONMENT;
  const limit = parseInt(searchParams.get("limit") || "10", 10);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery<SqlSyncPendingDataResponse>({
    queryKey: ["sqlSyncPendingData", entityType, environment, limit],
    queryFn: () => fetchSqlSyncPendingData(entityType || "", environment, limit || 10),
    enabled: Boolean(entityType),
  });

  const rows = (data?.rows || []).map(normalizeRow);

  const handleCopy = (row: any) => {
    navigator.clipboard.writeText(JSON.stringify(row, null, 2)).catch(() => {});
  };

  if (!entityType) {
    return (
      <ViewGuard section="scheduler" fallback={
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <div className="mb-4">
              <span className="material-icons text-6xl text-gray-400">block</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to view SQL Sync Pending</p>
          </div>
        </div>
      }>
        <PageWrapper>
          <h1 className="text-2xl font-bold mb-4">SQL Sync Pending</h1>
          <p className="text-red-500">Entity type is required</p>
        </PageWrapper>
      </ViewGuard>
    );
  }

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view SQL Sync Pending</p>
        </div>
      </div>
    }>
    <PageWrapper>
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Pending data: {entityType}</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setLocation("/sql-sync")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to SQL Sync
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>
        </div>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Pending changes snapshot</CardTitle>
              <div className="text-sm text-muted-foreground">
                Limit: {limit || 10}
              </div>
            </div>
            {data && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                  Total pending: {data.totalPending}
                </Badge>
                <Badge variant="outline">Rows shown: {rows.length}</Badge>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {isLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading pending data...
              </div>
            )}

            {isError && (
              <div className="text-red-600 text-sm">
                {(error as Error)?.message || "Failed to load pending data"}
              </div>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <div className="text-muted-foreground text-sm">No pending rows found.</div>
            )}

            {!isLoading && !isError && rows.length > 0 && (
              <div className="space-y-4">
                {rows.map((row, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-muted-foreground">Row {idx + 1}</div>
                      <Button variant="outline" size="sm" onClick={() => handleCopy(row)}>
                        <Copy className="h-3 w-3 mr-1" />
                        Copy JSON
                      </Button>
                    </div>
                    <pre className="text-xs bg-white p-3 rounded border overflow-auto">
                      {JSON.stringify(
                        Object.fromEntries(
                          Object.entries(row).map(([k, v]) => [k, v instanceof Date ? formatDate(v) : v])
                        ),
                        null,
                        2
                      )}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
    </ViewGuard>
  );
}
